import type { IconName } from '@nexus/ui';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useModules } from '../modules/context';

/** One hit of `GET /search`, see SearchService in @nexus/core-server. */
export interface SearchHit {
  kind: 'record' | 'account' | 'group';
  owner: string;
  entity: string;
  id: string;
  title: string;
  subtitle: string | null;
  path: string;
}

/** Shortest term the server answers. */
export const MIN_SEARCH_LENGTH = 2;

export function searchQuery(q: string, owner?: string) {
  const params = new URLSearchParams({ q });
  if (owner) params.set('owner', owner);
  return queryOptions({
    queryKey: ['search', q, owner ?? null],
    queryFn: ({ signal }) =>
      api.get<{ items: SearchHit[] }>(`/search?${params.toString()}`, { signal }),
    enabled: q.trim().length >= MIN_SEARCH_LENGTH,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

const KERNEL_ICONS: Record<Exclude<SearchHit['kind'], 'record'>, IconName> = {
  account: 'user',
  group: 'users',
};

/** Where a hit comes from: group heading, icon and module accent. */
export function useHitOrigin(): (hit: SearchHit) => {
  group: string;
  label: string;
  icon: IconName;
  accent?: string;
} {
  const { t } = useTranslation();
  const modules = useModules();
  return (hit) => {
    if (hit.kind !== 'record') {
      const label = t(`shell.search.kinds.${hit.kind}`);
      return { group: `core.${hit.kind}`, label, icon: KERNEL_ICONS[hit.kind] };
    }
    const module = modules.find((m) => m.manifest.id === hit.owner);
    return {
      group: hit.owner,
      label: module ? t(module.manifest.name) : hit.owner,
      icon: module?.manifest.icon ?? 'file',
      accent: module?.manifest.theme?.accent,
    };
  };
}

/** Groups hits by origin, keeping the order of the best hit of each group. */
export function groupHits<T extends { group: string }>(items: readonly T[]): T[][] {
  const groups = new Map<string, T[]>();
  for (const item of items) groups.set(item.group, [...(groups.get(item.group) ?? []), item]);
  return [...groups.values()];
}
